import { FC, memo, useEffect, useState } from 'react';
import classNames from 'classnames';
// Import Components
import { Icon } from '@geneui/components';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useAutocompleteRequest from 'hooks/UseAutocompleteRequest';
// Import Services
import { SegmentHttpService } from 'services/http';
// Import Enums
import { SegmentClientsCountRequestEnum } from 'enums';
// Import Styles
import styles from './segmentClientsCount.module.scss';

const INITIAL_COUNTS_VALUE = { total: '-', calculated: '-' };

const { getCompositeSegmentCount, getSegmentClientCount, getSegmentCount } = SegmentHttpService;

interface Props {
    config: {
        type: SegmentClientsCountRequestEnum;
        compositeSegmentConfig?: { Operation: string; Segments: number[] };
        segmentId?: number | string | null;
        filtersConfig?: AnyObject;
    };
    isDisabled?: boolean;
    resetClientsCountTrigger?: number;
}

const SegmentClientsCount: FC<Props> = ({ isDisabled = false, config, resetClientsCountTrigger }) => {
    const { doPostRequest, doGetRequest } = useRequest();
    const [clientsCount, setClientsCount] = useState(INITIAL_COUNTS_VALUE);
    const [countLoading, setCountLoading] = useState(false);

    const { getAutocompleteRequest, cancelRequest } = useAutocompleteRequest();

    const refreshClickHandler = () => {
        setCountLoading(true);

        switch (config.type) {
            case SegmentClientsCountRequestEnum.CompositeSegment:
                {
                    const requestObj = getAutocompleteRequest(getCompositeSegmentCount);

                    doPostRequest(requestObj.request, {
                        requestBody: { Config: config.compositeSegmentConfig },
                        successCallback: (data) => setClientsCount({ total: data.Total, calculated: data.Count }),
                    }).then(() => {
                        setCountLoading(false);
                    });
                }
                break;
            case SegmentClientsCountRequestEnum.SegmentId:
                {
                    const requestObj = getAutocompleteRequest(getSegmentClientCount);

                    doGetRequest(requestObj.request, {
                        queryString: { segmentId: config.segmentId },
                        successCallback: (data) => setClientsCount({ total: data.Total, calculated: data.Count }),
                    }).then(() => {
                        setCountLoading(false);
                    });
                }
                break;
            case SegmentClientsCountRequestEnum.Filter:
                {
                    const requestObj = getAutocompleteRequest(getSegmentCount);

                    doPostRequest(requestObj.request, {
                        requestBody: config.filtersConfig,
                        successCallback: (data) => setClientsCount({ total: data.Total, calculated: data.Count }),
                    }).then(() => {
                        setCountLoading(false);
                    });
                }
                break;
        }
    };

    useEffect(() => {
        setClientsCount(INITIAL_COUNTS_VALUE);
        setCountLoading(false);
        cancelRequest('SegmentClientsCount');
    }, [
        config.segmentId,
        config.compositeSegmentConfig?.Operation,
        config.compositeSegmentConfig?.Segments,
        config.filtersConfig,
        resetClientsCountTrigger,
        cancelRequest,
    ]);

    const cleanUp = () => {
        return () => {
            cancelRequest('SegmentClientsCount');
        };
    };

    useEffect(cleanUp, [cancelRequest]);

    return (
        <div className={styles.mainContainer}>
            <p>{`${clientsCount.calculated} / ${clientsCount.total}`}</p>
            <Icon
                disabled={isDisabled}
                className={classNames(styles.refreshIcon, {
                    [styles.refreshIconRotate]: countLoading,
                })}
                type={'bc-icon-refresh'}
                onClick={refreshClickHandler}
            />
        </div>
    );
};

export default memo(SegmentClientsCount);
